import { useState } from 'react'
import { Row, Col, Card, Form, Button, Alert } from 'react-bootstrap'
import { useAuth } from '../hooks/useAuth'
import { api } from '../services/api'

export const ContactSupportPage = () => {
  const { user } = useAuth()
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    priority: 'Medium'
  })
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setError('')
    setSuccess(false)

    try {
      // Create a support ticket
      await api.post('/tickets', {
        subject: `Support Request: ${formData.subject}`,
        description: formData.message,
        priority: formData.priority,
        category: 'General',
        requesterId: user?.id
      })

      setSuccess(true)
      setFormData({
        subject: '',
        message: '',
        priority: 'Medium'
      })
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to submit support request')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="contact-support-page">
      {/* Header */}
      <div className="mb-4">
        <h2 className="mb-2">
          <i className="fas fa-headset me-2 text-primary"></i>
          Contact Support
        </h2>
        <p className="text-muted mb-0">
          Get in touch with our support team. We're here to help!
        </p>
      </div>

      <Row>
        <Col lg={8}>
          {/* Contact Form */}
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom">
              <h5 className="mb-0">
                <i className="fas fa-envelope me-2 text-primary"></i>
                Send Us a Message
              </h5>
            </Card.Header>
            <Card.Body className="p-4">
              {success && (
                <Alert variant="success" className="mb-4" onClose={() => setSuccess(false)} dismissible>
                  <i className="fas fa-check-circle me-2"></i>
                  Your support request has been submitted successfully! We'll get back to you soon.
                </Alert>
              )}

              {error && (
                <Alert variant="danger" className="mb-4" onClose={() => setError('')} dismissible>
                  <i className="fas fa-exclamation-circle me-2"></i>
                  {error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Your Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={user?.name || ''}
                    disabled
                    className="bg-light"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Your Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="bg-light"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Priority <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    required
                  >
                    <option value="Low">Low - General inquiry</option>
                    <option value="Medium">Medium - Need assistance</option>
                    <option value="High">High - Issue affecting work</option>
                    <option value="Urgent">Urgent - Critical issue</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Subject <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Brief description of your issue"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Message <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    placeholder="Please provide details about your issue or question..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                  />
                  <Form.Text className="text-muted">
                    Please provide as much detail as possible to help us assist you better.
                  </Form.Text>
                </Form.Group>

                <div className="d-flex justify-content-end">
                  <Button
                    variant="primary"
                    type="submit"
                    size="lg"
                    disabled={submitting}
                    className="px-4"
                  >
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane me-2"></i>
                        Send Message
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          {/* Support Info */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body>
              <h6 className="mb-3">
                <i className="fas fa-clock me-2 text-primary"></i>
                Response Time
              </h6>
              <p className="text-muted small mb-0">
                We aim to respond to all inquiries within 24 hours during business days.
              </p>
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm mb-4">
            <Card.Body>
              <h6 className="mb-3">
                <i className="fas fa-life-ring me-2 text-primary"></i>
                Quick Help
              </h6>
              <p className="text-muted small mb-2">
                Before contacting support, you might find answers in:
              </p>
              <ul className="small text-muted">
                <li>Knowledge Base</li>
                <li>FAQs</li>
                <li>Documentation</li>
              </ul>
            </Card.Body>
          </Card>

          <Card className="border-0 bg-light">
            <Card.Body>
              <h6 className="mb-3">
                <i className="fas fa-question-circle me-2"></i>
                Need Urgent Help?
              </h6>
              <p className="text-muted small mb-0">
                For critical issues, please select "Urgent" priority and we'll prioritize your request.
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  )
}





